import { useState, useCallback } from "react";
import { message } from "antd";

//const API_URL = "https://www.inoradigi.com/api";
const API_URL = "https://inora.webthoughts.in/api";
//const API_URL = "http://localhost:4000/api";
/**
 * Custom hook for downloading files from API endpoints
 * Handles binary responses (PDF, Excel, etc.) with proper blob handling
 *
 * @param {string} apiPath - API endpoint path
 * @param {string} defaultMethod - Default HTTP method (usually POST)
 * @returns {Array} [state, downloadFile] - State object and download function
 */
const useFileDownload = (apiPath, defaultMethod = "POST") => {
  const [state, setState] = useState({
    isLoading: false,
    isError: false,
    error: null,
    progress: 0,
  });

  /**
   * Downloads a file from the API endpoint
   * @param {Object} body - Request body to send to API
   * @param {string} filename - Name for the downloaded file (optional)
   * @param {string} method - HTTP method override (optional)
   * @returns {Promise<boolean>} - Returns true if download successful, false otherwise
   */
  const downloadFile = useCallback(
    async (body, filename = null, method = null) => {
      const httpMethod = method || defaultMethod;
      const authToken = localStorage.getItem("authToken");

      // Construct full URL
      const fullUrl = API_URL + apiPath;

      // Prepare request options
      const options = {
        method: httpMethod,
        headers: {
          "Content-Type": "application/json",
          Authorization: authToken,
        },
      };

      // Add body for POST/PUT methods
      if (httpMethod !== "GET" && body) {
        options.body = JSON.stringify(body);
      }

      // Set loading state
      setState({
        isLoading: true,
        isError: false,
        error: null,
        progress: 0,
      });

      try {
        const response = await fetch(fullUrl, options);

        // Handle 401 Unauthorized
        if (response.status === 401) {
          localStorage.clear();
          window.location.href = "/";
          return false;
        }

        // Check if response is successful
        if (!response.ok) {
          // Try to parse error as JSON (backend might return JSON error)
          let errorMessage = `Download failed with status: ${response.status}`;

          try {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
              const errorData = await response.json();
              errorMessage =
                errorData.error || errorData.message || errorMessage;
            }
          } catch (parseError) {
            // If JSON parse fails, use default error message
            console.error("Could not parse error response:", parseError);
          }

          setState({
            isLoading: false,
            isError: true,
            error: errorMessage,
            progress: 0,
          });

          message.error(errorMessage);
          return false;
        }

        // Get blob from response
        const blob = await response.blob();

        // Check if blob is empty
        if (blob.size === 0) {
          const errorMsg = "Downloaded file is empty";
          setState({
            isLoading: false,
            isError: true,
            error: errorMsg,
            progress: 0,
          });
          message.error(errorMsg);
          return false;
        }

        // Extract filename from Content-Disposition header if not provided
        let downloadFilename = filename;
        if (!downloadFilename) {
          const contentDisposition = response.headers.get(
            "Content-Disposition"
          );
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch && filenameMatch[1]) {
              downloadFilename = filenameMatch[1];
            }
          }
        }

        // Fallback filename if still not set
        if (!downloadFilename) {
          const timestamp = new Date().getTime();
          downloadFilename = `download-${timestamp}.pdf`;
        }

        // Create blob URL and trigger download
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = downloadFilename;
        link.style.display = "none";

        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up blob URL
        window.URL.revokeObjectURL(blobUrl);

        // Set success state
        setState({
          isLoading: false,
          isError: false,
          error: null,
          progress: 100,
        });

        return true;
      } catch (error) {
        console.error("File download error:", error);

        const errorMessage = error.message || "Network error during download";

        setState({
          isLoading: false,
          isError: true,
          error: errorMessage,
          progress: 0,
        });

        message.error(errorMessage);
        return false;
      }
    },
    [apiPath, defaultMethod]
  );

  return [state, downloadFile];
};

export default useFileDownload;
